import type { FeedType } from '@kanbu/schema';
import { FeedHealth, FeedStatus, ShopProductStatus } from '@kanbu/schema/enums';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { useLingui } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { createColumnHelper } from '@tanstack/react-table';
import { Badge, Button } from '@utima/ui';
import {
  DatabaseBackup,
  Package,
  Settings,
  Tags,
  Pause,
  Play,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { ConfirmDialog } from '@/components/confirmDialog/ConfirmDialog';
import { ActionsCell } from '@/components/dataTable/cells/ActionsCell';
import { IconWrapper } from '@/components/iconWrapper/IconWrapper';
import { SimpleTooltip } from '@/components/index';
import { useEnumTranslations } from '@/hooks';

import { getFeedAgents } from './feedAgents';
import { FeedImportProgress } from './FeedImportProgress';
import { useFeedMutation } from './useFeedMutation';

const columnHelper = createColumnHelper<FeedType>();

export type UseFeedsColsOptions = {
  chatId: string;
  onOpenSettings: (feedId: string) => void;
};

type FeedActionsCellProps = {
  feed: FeedType;
  chatId: string;
  onOpenSettings: (feedId: string) => void;
  onRefresh: () => Promise<unknown> | unknown;
};

function FeedActionsCell({
  feed,
  chatId,
  onOpenSettings,
  onRefresh,
}: FeedActionsCellProps) {
  const { t } = useLingui();
  const {
    setProcessingPaused,
    remove: { mutateAsync: remove },
    update: { mutateAsync: update },
  } = useFeedMutation();
  const otherAgents = getFeedAgents(feed).filter(agent => agent.id !== chatId);
  const otherAgentNames = otherAgents.map(agent => agent.name).join(', ');

  /**
   * A feed other agents still use is only taken off this one; deleting it
   * would empty their catalogs too. Deleting a shared feed is an explicit
   * action in its settings.
   */
  const handleDelete = () =>
    otherAgents.length > 0
      ? update({
          id: feed.id,
          chatIds: otherAgents.map(agent => agent.id),
        })
      : remove({ id: feed.id });
  const processing = [
    FeedStatus.Pending,
    FeedStatus.Processing,
    FeedStatus.Embedding,
  ].includes(feed.status);
  const [refreshConfirmOpen, setRefreshConfirmOpen] = useState(false);

  return (
    <ActionsCell
      onDelete={handleDelete}
      deleteDescription={
        otherAgents.length > 0
          ? t`Remove "${feed.name}" from this agent? ${otherAgentNames} will keep using it.`
          : undefined
      }
    >
      {(processing || feed.processingPaused) && (
        <SimpleTooltip
          title={
            feed.processingPaused
              ? t`Resume processing`
              : t`Pause after the current batch`
          }
        >
          <Button
            variant='ghost'
            outline
            size='icon-xs'
            aria-label={
              feed.processingPaused ? t`Resume processing` : t`Pause processing`
            }
            disabled={setProcessingPaused.isPending}
            onClick={() =>
              setProcessingPaused.mutate({
                id: feed.id,
                paused: !feed.processingPaused,
              })
            }
          >
            {feed.processingPaused ? (
              <Play className='size-4' />
            ) : (
              <Pause className='size-4' />
            )}
          </Button>
        </SimpleTooltip>
      )}
      <ConfirmDialog
        open={refreshConfirmOpen}
        onOpenChange={setRefreshConfirmOpen}
        onConfirm={onRefresh}
        title={t`Refresh feed data?`}
        description={t`"${feed.name}" will be re-imported from its source. This can take a few minutes before the status updates.`}
        confirmLabel={t`Refresh`}
        confirmVariant='primary'
        successTitle={t`Feed queued for refresh`}
        successDescription={t`It will be re-imported shortly.`}
      />
      <SimpleTooltip title={t`Attributes`}>
        <Button
          data-cta='admin-feed-attributes-button'
          variant='ghost'
          outline
          size='icon-xs'
          asChild
        >
          <Link
            to='/chats/$chatId/feeds/$feedId/attributes'
            params={{ chatId, feedId: feed.id }}
            aria-label={t`Attributes`}
          >
            <Tags className='size-4' />
          </Link>
        </Button>
      </SimpleTooltip>
      <SimpleTooltip title={t`Settings`}>
        <Button
          data-cta='admin-feed-settings-button'
          variant='ghost'
          outline
          size='icon-xs'
          onClick={() => onOpenSettings(feed.id)}
        >
          <Settings className='size-4' />
        </Button>
      </SimpleTooltip>
      <SimpleTooltip title={t`Refresh data`}>
        <Button
          data-cta='admin-feed-refresh-data-button'
          disabled={processing || feed.processingPaused}
          variant='ghost'
          className='text-info'
          outline
          size='icon-xs'
          onClick={() => setRefreshConfirmOpen(true)}
        >
          <DatabaseBackup className='size-4' />
        </Button>
      </SimpleTooltip>
    </ActionsCell>
  );
}

export function useFeedsCols({ chatId, onOpenSettings }: UseFeedsColsOptions) {
  const { t } = useLingui();
  const {
    update: { mutateAsync: update },
  } = useFeedMutation();
  const enumTranslations = useEnumTranslations();

  return useMemo(
    () => [
      columnHelper.accessor(row => row.name, {
        id: 'name',
        header: t`Name`,
        cell: ({ row }) => {
          const data = row.original;

          return (
            <div className='flex min-w-64 items-center gap-3'>
              <IconWrapper size='sm' className='text-danger' Icon={Package} />
              <div className='flex flex-col gap-0.5'>
                <p className='break-all font-medium leading-tight'>
                  {data.name}
                </p>
                {data.url && (
                  <p className='hidden text-xs text-gray-600 md:block'>
                    {data.url}
                  </p>
                )}
                {(data.health === FeedHealth.Failed ||
                  data.health === FeedHealth.Stale ||
                  data.health === FeedHealth.Degraded) && (
                  <SimpleTooltip
                    title={data.importMetadata?.productErrors
                      ?.map(error => `${error.externalId}: ${error.message}`)
                      .join('\n')}
                  >
                    <span
                      className={
                        data.health === FeedHealth.Failed
                          ? 'text-xs text-danger'
                          : 'text-xs text-amber-600'
                      }
                    >
                      {data.importMetadata?.lastError}
                    </span>
                  </SimpleTooltip>
                )}
              </div>
            </div>
          );
        },
        meta: {
          sortable: 'name',
        },
      }),
      columnHelper.accessor(row => row.source, {
        id: 'source',
        header: t`Source`,
        cell: ({ row }) => (
          <Badge variant='primary' outline size='sm'>
            {enumTranslations.feedSource[row.original.source]}
          </Badge>
        ),
        meta: {
          sortable: 'source',
        },
      }),
      columnHelper.accessor(row => row.finishedImportAt, {
        id: 'finishedImportAt',
        header: t`Last imported`,
        cell: row =>
          row.getValue()
            ? formatUtils.date(row.getValue()!, DateFormat.DateTime)
            : '-',
        meta: {
          sortable: 'finishedImportAt',
        },
      }),
      columnHelper.display({
        id: 'productsStatus',
        header: t`Status`,
        cell: ({ row }) => {
          const feedId = row.original.id;

          const segments: {
            key: string;
            status?: ShopProductStatus;
            color: string;
            count: number | string;
            label: string;
          }[] = [
            {
              key: 'pending',
              status: ShopProductStatus.Pending,
              color: 'bg-yellow-400',
              count: row.original.pendingProductsCount,
              label: t`Pending`,
            },
            {
              key: 'embedding',
              status: ShopProductStatus.Embedding,
              color: 'bg-blue-400',
              count: row.original.embeddingProductsCount,
              label: t`Embedding`,
            },
            {
              key: 'done',
              status: ShopProductStatus.Done,
              color: 'bg-green-400',
              count: row.original.doneProductsCount,
              label: t`Done`,
            },
            {
              key: 'failed',
              status: ShopProductStatus.Failed,
              color: 'bg-red-400',
              count: row.original.failedProductsCount,
              label: t`Failed`,
            },
          ];

          return (
            <div
              data-table='prevent'
              className='flex flex-col gap-1 rounded-md border bg-gray-50 px-3 py-1.5'
            >
              <FeedImportProgress feed={row.original} />
              {segments.map(segment => (
                <Link
                  key={segment.key}
                  data-cta={`admin-feed-products-${segment.key}-link`}
                  to='/chats/$chatId/feeds/$feedId/products'
                  params={{ chatId, feedId }}
                  search={{ status: segment.status }}
                  className='flex items-center gap-1.5 text-xs hover:underline'
                >
                  <div className={`size-2 rounded-full ${segment.color}`} />
                  <span className='font-medium'>{segment.count}</span>
                  <span className='text-gray-500'>{segment.label}</span>
                </Link>
              ))}
            </div>
          );
        },
        meta: {
          align: 'center',
        },
      }),
      columnHelper.display({
        id: 'agents',
        header: t`Agents`,
        cell: ({ row }) => {
          const agents = getFeedAgents(row.original);

          return (
            <span
              className='text-xs text-muted-fg'
              title={agents.map(agent => agent.name).join(', ')}
            >
              {agents.length}
            </span>
          );
        },
        meta: {
          align: 'center',
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: ({ row }) => (
          <FeedActionsCell
            feed={row.original}
            chatId={chatId}
            onOpenSettings={onOpenSettings}
            onRefresh={() =>
              update({
                id: row.original.id,
                status: FeedStatus.Pending,
                importMetadata: null,
                content: null,
                scheduledAt: new Date(),
              })
            }
          />
        ),
        meta: {
          align: 'center',
        },
      }),
    ],
    [t, chatId, enumTranslations.feedSource, update, onOpenSettings],
  );
}
